import * as React from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Drawer, useMediaQuery, useTheme } from '@material-ui/core';
import { HelpPanel } from '../components/HelpPanel';
import { Header } from '../components/Header';
import { useAppStateContext } from '../contexts/AppState';
import { Question } from 'survey-core';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    mainContainer: {
      width: '100%',
      height: '100%',
      display: 'flex',
      [theme.breakpoints.down('md')]: {
        flexDirection: 'column'
      }
    },
    headerContainer: {
      display: 'none',
      position: 'fixed',
      width: '100%',
      height: 64,
      backgroundColor: 'var(--light-blue)',
      zIndex: theme.zIndex.modal + 1,
      [theme.breakpoints.down('md')]: {
        display: 'block'
      }
    },
    sidebarContainer: {
      width: 344,
      height: '100%',
      maxHeight: '100%',
      backgroundColor: 'var(--light-blue)',
      overflow: 'auto'
    },
    drawer: {
      width: 344
    },
    drawerPaper: {
      borderRight: 'none',
      [theme.breakpoints.down('md')]: {
        paddingTop: 64,
        boxSizing: 'border-box'
      }
    },
    contentContainer: {
      width: '100%',
      maxHeight: '100%',
      flex: '1',
      overflow: 'auto',
      padding: theme.spacing(1),
      boxSizing: 'border-box',
      [theme.breakpoints.down('md')]: {
        padding: '64px 0 0',
        boxSizing: 'border-box'
      }
    }
  })
);

type Props = {
  firstInputQuestion?: Question | null;
  focusFirstControl?: boolean;
  setFocusFirstControl?(arg: boolean): void;
};

export const MainLayout: React.FC<Props> = ({
  firstInputQuestion,
  focusFirstControl,
  setFocusFirstControl,
  children
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showSidebar, setShowSidebar] = React.useState(false);
  const { updateSetScrollPosition } = useAppStateContext();
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    updateSetScrollPosition((position: number) => containerRef.current && containerRef.current.scrollTo(0, position));
  }, [updateSetScrollPosition]);

  const onToggle = () => {
    setShowSidebar(!showSidebar);
  };
  const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Tab' && focusFirstControl) {
      if (firstInputQuestion?.questionRef?.current) {
        firstInputQuestion.questionRef.current.focus();
      }
      setFocusFirstControl && setFocusFirstControl(false);
    }
  };

  return (
    <div className={classes.mainContainer} onKeyUp={handleKeyUp}>
      <div className={classes.headerContainer}>
        <Header showSidebar={showSidebar} onToggle={onToggle} />
      </div>
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={showSidebar}
        classes={{ root: classes.drawer, paper: classes.drawerPaper }}
      >
        <div className={classes.sidebarContainer}>
          <HelpPanel />
        </div>
      </Drawer>
      <div id="contentContainer" className={classes.contentContainer} ref={containerRef}>
        {children}
      </div>
    </div>
  );
};
